import { useCallback } from 'react'

import { Segment } from 'core/analytics/segment'

import { TRACKING_EVENT_NAMES } from './constants'
import { useTrack } from './useTrack'

/**
 * Tracks the CHECKOUT_FLOW_STARTED event with standardized properties.
 *
 * @param {Object} options - Tracking options
 * @param {string} options.checkoutFlowLocation - Where the checkout flow was initiated
 * @param {string|null} [options.checkoutPlanName] - The plan name being checked out
 * @param {string|null} [options.checkoutPlanBillingTerm] - The billing term
 * @param {number|undefined} [options.daysSinceTrialStart] - Optional days since trial started
 * @param {string|undefined} [options.quotaName] - Optional quota name
 * @param {boolean|undefined} [options.quotaReached] - Optional whether user reached quota
 * @returns {Function} A function that can be called to track the event
 */
export const useTrackCheckoutFlowStarted = () => {
  const track = useTrack()

  return useCallback(({
    checkoutFlowLocation,
    checkoutPlanName = null,
    checkoutPlanBillingTerm = null,
    daysSinceTrialStart,
    quotaName,
    quotaReached
  }) => {
    const properties = {
      checkout_flow_location: checkoutFlowLocation,
      checkout_plan_name: checkoutPlanName,
      checkout_plan_billing_term: checkoutPlanBillingTerm,
      days_since_trial_start: daysSinceTrialStart,
    }

    if (quotaName?.trim() != null) {
      properties.quota_name = quotaName.trim()
    }
    if (quotaReached != null) {
      properties.quota_reached = quotaReached
    }

    // Segment v2 (server-mode) for Segment
    track({
      name: TRACKING_EVENT_NAMES.CHECKOUT_FLOW_STARTED,
      properties,
    })

    // Segment v1 (device-mode) for Braze
    Segment.track(TRACKING_EVENT_NAMES.CHECKOUT_FLOW_STARTED, properties)
  }, [track])
}
